import { Notification } from '../services/Notification.js';
import { translate } from './i18n.js';

export class EventEditor {
    constructor(events) {
        this.events = events;
        this.app = events.app;
        this.locale = events.locale;
        this.event = null;
        this.successCloseTimer = null;
        this.fieldSequence = 0;
        this.organisations = null;
    }

    getContents() {
        return {
            eventEditor: {
                titleKey: 'eventEditor.createTitle',
                icon: this.event ? '/images/update-grey.svg' : '/images/event-add-grey.svg',
                path: 'events/add',
                attachedLabel: true,
                render: (context) => this.render(context),
                cleanup: () => this.cleanup()
            }
        };
    }

    open(event = null, returnFocus = null) {
        this.event = event;
        if (!this.app?.simpleModal) return false;

        this.app.simpleModal.contents.eventEditor = this.getContents().eventEditor;
        return this.app.simpleModal.open('eventEditor', returnFocus);
    }

    render(context) {
        const { body, setTitle } = context;
        const event = this.event;
        const form = document.createElement('form');
        const intro = document.createElement('p');

        setTitle(this.t(event ? 'eventEditor.updateTitle' : 'eventEditor.createTitle'));
        form.className = 'ui-form event-editor-form';
        intro.className = 'intro event-editor-intro';
        intro.textContent = this.t('eventEditor.intro');
        const fields = [
            this.createTextField('name', 'eventEditor.name', {
                value: this.localized(event?.name),
                required: true,
                maxlength: 180
            }),
            this.createTextField('description', 'eventEditor.description', {
                type: 'textarea',
                value: this.localized(event?.description),
                required: true
            }),
            this.createOrganizerField(context),
            event ? this.createTextField('source', 'eventEditor.source', {
                value: event?.source || '',
                maxlength: 2048,
                autocomplete: 'url'
            }) : null,
            this.createTextField('external_link', 'eventEditor.externalLink', {
                type: 'url',
                value: event?.external_link || '',
                maxlength: 2048,
                autocomplete: 'url'
            }),
            this.createTagsSection(),
            this.createLocationSection(),
            this.createDatesSection(),
            this.createIllustrationSection(),
            this.createVisibilitySection()
        ].filter(Boolean);
        form.append(intro, ...fields);

        const actions = document.createElement('div');
        const remove = event ? document.createElement('button') : null;
        const submit = document.createElement('button');

        actions.className = 'ui-form-buttons event-editor-actions';
        if (remove) {
            const removeIcon = document.createElement('img');
            const removeLabel = document.createElement('span');

            remove.type = 'button';
            remove.className = 'ui-buttons medium delete';
            removeIcon.src = '/images/trash-red.svg';
            removeIcon.alt = '';
            removeIcon.setAttribute('aria-hidden', 'true');
            removeLabel.textContent = this.t('eventEditor.deleteAction');
            remove.append(removeIcon, removeLabel);
            remove.addEventListener('click', () => this.confirmDelete(remove, context));
        }
        submit.type = 'submit';
        submit.className = 'ui-buttons big';
        const submitIcon = document.createElement('img');
        const submitLabel = document.createElement('span');

        submitIcon.src = event ? '/images/update-grey.svg' : '/images/event-add-grey.svg';
        submitIcon.alt = '';
        submitIcon.setAttribute('aria-hidden', 'true');
        submitLabel.textContent = this.t(event ? 'eventEditor.updateAction' : 'eventEditor.createAction');
        submit.append(submitIcon, submitLabel);
        actions.append(submit);
        if (remove) actions.append(remove);
        form.append(actions);
        form.addEventListener('submit', (submitEvent) => {
            submitEvent.preventDefault();
            void this.submit(form, submit, context);
        });
        body.append(form);
    }

    createOrganizerField(context) {
        const selectedUid = String(
            this.event?.organizer?.uid
            || this.event?.uid_organizer
            || (!this.event ? this.app?.user?.organisme : '')
            || ''
        ).trim();
        const field = this.createTextField('uid_organizer', 'eventEditor.organizer', {
            type: 'select',
            value: selectedUid,
            options: [{
                value: '',
                label: this.t('eventEditor.organizerLoading'),
                disabled: true
            }]
        });
        const select = field.querySelector('select');

        if (this.organisations) {
            this.populateOrganizerOptions(select, selectedUid);
        } else {
            void this.loadOrganisations(select, selectedUid, context);
        }

        return field;
    }

    async loadOrganisations(select, selectedUid, context) {
        const succeeded = await this.events.api.query(
            'get',
            '/organismes/list',
            {},
            () => context.showLoading(),
            (response) => {
                this.organisations = Array.isArray(response?.result?.organismes)
                    ? response.result.organismes
                    : [];
                if (select?.isConnected) {
                    this.populateOrganizerOptions(select, selectedUid);
                }
                context.hideLoading();
            },
            null,
            {
                onError: () => {
                    context.hideLoading();
                    new Notification({
                        icon: 'warning.svg',
                        title: this.t('eventEditor.errorTitle'),
                        content: this.t('eventEditor.organizerLoadError')
                    });
                }
            }
        );

        return succeeded;
    }

    populateOrganizerOptions(select, selectedUid = '') {
        if (!select) return;

        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = this.t('eventEditor.organizerPlaceholder');
        placeholder.selected = !selectedUid;
        const collator = new Intl.Collator(this.locale, { sensitivity: 'base' });
        const options = [...(this.organisations || [])]
            .sort((organisationA, organisationB) => collator.compare(
                String(organisationA?.name || organisationA?.nom || '').trim(),
                String(organisationB?.name || organisationB?.nom || '').trim()
            ))
            .map((organisation) => {
            const option = document.createElement('option');
            const uid = String(organisation?.uid || organisation?.uid_organismes || '').trim();
            const country = String(organisation?.country || organisation?.pays || '').trim().toUpperCase();

            option.value = uid;
            option.textContent = country
                ? `${String(organisation?.name || organisation?.nom || '').trim()} — ${country}`
                : String(organisation?.name || organisation?.nom || '').trim();
            option.selected = uid === selectedUid;
            return option;
        }).filter((option) => option.value && option.textContent);

        select.replaceChildren(placeholder, ...options);
        if (selectedUid && options.some((option) => option.value === selectedUid)) {
            select.value = selectedUid;
        } else {
            select.value = '';
        }
        select.dispatchEvent(new Event('change'));
    }

    createTextField(name, labelKey, options = {}) {
        const field = document.createElement('div');
        const label = document.createElement('label');
        const inputId = this.getFieldId(name);
        let input;

        field.className = `ui-form-element event-editor-field event-editor-field-${name.replaceAll('_', '-')}`;
        if (options.width) field.classList.add(`ui-form-element-width-${options.width}`);
        label.className = 'ui-input-label event-editor-label';
        label.htmlFor = inputId;
        label.textContent = this.t(labelKey);

        if (options.type === 'textarea') {
            input = document.createElement('textarea');
            input.className = 'ui-textarea';
            input.rows = 6;
        } else if (options.type === 'select') {
            input = document.createElement('select');
            input.className = 'ui-select';
            (options.options || []).forEach(({ value, label: optionLabel, disabled = false }) => {
                const option = document.createElement('option');

                option.value = value;
                option.textContent = optionLabel;
                option.disabled = disabled;
                option.selected = value === (options.value || '');
                input.append(option);
            });
        } else {
            input = document.createElement('input');
            input.type = options.type || 'text';
            input.className = 'ui-input';
            if (options.type === 'date') input.classList.add('ui-input-date');
        }

        input.id = inputId;
        input.name = name;
        if (options.value !== undefined && options.type !== 'select') input.value = options.value || '';
        if (options.required) input.required = true;
        if (options.maxlength) input.maxLength = options.maxlength;
        if (options.autocomplete) input.autocomplete = options.autocomplete;
        const syncValueState = () => {
            input.classList.toggle('has-value', String(input.value || '').trim() !== '');
        };

        input.addEventListener('input', syncValueState);
        input.addEventListener('change', syncValueState);
        syncValueState();
        field.append(label, input);
        return field;
    }

    getFieldId(name) {
        this.fieldSequence += 1;
        return `event-editor-${name}-${this.fieldSequence}`;
    }

    createTagsSection() {
        const section = this.createSection('eventEditor.tags');
        const disciplines = this.events.disciplines.map((id) => ({
            id,
            label: this.events.disciplineLabels[id],
            color: this.events.getDisciplineTagColor(id)
        }));
        const additionalTags = [...this.events.getConfiguredEventTags().values()].map((tag) => ({
            id: String(tag.id),
            label: this.events.getLocalizedValue(tag.label),
            color: tag.color
        }));

        section.append(
            this.createTagGroup('eventEditor.disciplines', disciplines, true),
            this.createTagGroup('eventEditor.additionalTags', additionalTags)
        );
        return section;
    }

    createTagGroup(labelKey, options, requireOne = false) {
        const group = document.createElement('fieldset');
        const legend = document.createElement('legend');
        const choices = document.createElement('div');
        const selectedTags = new Set(this.event?.tags || []);

        group.className = 'ui-form-element event-editor-choice-group';
        group.dataset.requireOne = requireOne ? 'true' : 'false';
        legend.textContent = this.t(labelKey);
        choices.className = 'event-editor-choices calendar-filter-tags';

        options.forEach(({ id, label, color }) => {
            const choice = document.createElement('label');
            const input = document.createElement('input');
            const visual = document.createElement('span');
            const sync = () => {
                visual.classList.toggle('active', input.checked);
                visual.setAttribute('aria-pressed', String(input.checked));
            };

            choice.className = 'event-editor-tag';
            input.type = 'checkbox';
            input.name = 'tags';
            input.value = id;
            input.checked = selectedTags.has(id);
            visual.className = 'calendar-filter-tag';
            visual.style.setProperty('--calendar-filter-tag-color', color || '#667481');
            visual.textContent = label;
            input.addEventListener('change', sync);
            choice.append(input, visual);
            choices.append(choice);
            sync();
        });
        group.append(legend, choices);
        return group;
    }

    createLocationSection() {
        const section = this.createSection('eventEditor.location');
        const grid = document.createElement('div');

        grid.className = 'ui-form event-editor-grid event-editor-grid-location';
        grid.append(
            this.createTextField('address', 'eventEditor.address', {
                value: this.event?.address || '',
                autocomplete: 'street-address'
            }),
            this.createTextField('city', 'eventEditor.city', {
                value: this.event?.city || '',
                required: true,
                autocomplete: 'address-level2',
                width: 50
            }),
            this.createTextField('country', 'eventEditor.country', {
                type: 'select',
                value: this.normalizeCountryCode(this.event?.country),
                required: true,
                width: 50,
                options: this.getCountryOptions()
            })
        );
        section.append(grid);
        return section;
    }

    createDatesSection() {
        const section = this.createSection('eventEditor.dates');
        const grid = document.createElement('div');

        grid.className = 'ui-form event-editor-grid event-editor-grid-dates';
        grid.append(
            this.createTextField('start_date', 'eventEditor.startDate', {
                type: 'date',
                value: this.event?.start_date || '',
                required: true,
                width: 50
            }),
            this.createTextField('end_date', 'eventEditor.endDate', {
                type: 'date',
                value: this.event?.end_date || '',
                width: 50
            })
        );
        section.append(grid);
        return section;
    }

    createIllustrationSection() {
        const section = this.createSection('eventEditor.illustration');
        const field = document.createElement('div');
        const editor = document.createElement('div');
        const preview = document.createElement('button');
        const fileInput = document.createElement('input');
        const removeInput = document.createElement('input');
        const trash = document.createElement('button');
        const trashIcon = document.createElement('img');
        const help = document.createElement('p');
        const fallbackSources = ['organizer_logo', 'federation_logo', 'discipline_fallback'];
        const hasDedicatedIllustration = Boolean(this.event?.illustration)
            && !fallbackSources.includes(String(this.event?.illustration_source || '').toLowerCase());
        const currentImage = this.event ? this.events.getEventImageUrl(this.event) : '';
        const inputId = this.getFieldId('illustration');

        field.className = 'ui-form-element ui-input-photo-container-simple event-editor-illustration-field';
        editor.className = 'ui-input-photo-instance-simple event-editor-image';
        preview.type = 'button';
        preview.className = 'ui-input-photo-preview event-editor-image-preview';
        preview.setAttribute('aria-label', this.t('eventEditor.illustrationChoose'));
        fileInput.type = 'file';
        fileInput.id = inputId;
        fileInput.name = 'illustration';
        fileInput.accept = 'image/jpeg,image/png,image/gif';
        fileInput.className = 'ui-input-photo-file event-editor-file-input';
        fileInput.hidden = true;
        removeInput.type = 'hidden';
        removeInput.name = 'remove_illustration';
        removeInput.value = '0';
        trash.type = 'button';
        trash.className = 'ui-input-photo-btn ui-input-photo-btn-trash event-editor-image-trash';
        trash.setAttribute('aria-label', this.t('eventEditor.illustrationReset'));
        trashIcon.src = '/images/trash-grey.svg';
        trashIcon.alt = '';
        trashIcon.setAttribute('aria-hidden', 'true');
        trash.append(trashIcon);
        help.className = 'event-editor-help';
        help.textContent = this.t('eventEditor.illustrationHelp');

        const setPreview = (source = '') => {
            preview.style.backgroundImage = source ? `url("${source}")` : '';
            preview.style.backgroundSize = source ? 'cover' : '40px';
            editor.classList.toggle('set', Boolean(source));
            editor.classList.toggle('has-image', Boolean(source));
        };
        preview.addEventListener('click', () => fileInput.click());
        fileInput.addEventListener('change', () => {
            const file = fileInput.files?.[0];

            if (!file) return;
            if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
                fileInput.value = '';
                new Notification({
                    icon: 'warning.svg',
                    title: this.t('eventEditor.errorTitle'),
                    content: this.t('eventEditor.illustrationTypeError')
                });
                return;
            }
            removeInput.value = '0';
            setPreview(URL.createObjectURL(file));
        });
        trash.addEventListener('click', () => {
            fileInput.value = '';
            removeInput.value = hasDedicatedIllustration ? '1' : '0';
            setPreview();
        });

        setPreview(currentImage);
        editor.append(preview, fileInput, removeInput, trash);
        field.append(editor);
        section.append(field, help);
        return section;
    }

    createVisibilitySection() {
        const section = this.createSection('eventEditor.visibility');
        const group = document.createElement('div');
        const currentVisibility = this.event?.status === 'published'
            ? 'public'
            : (this.event?.status === 'public_draft' ? 'public_draft' : 'draft');

        group.className = 'ui-form-element event-editor-visibility-options';
        [
            ['public', 'eventEditor.visibilityPublic', 'eventEditor.visibilityPublicHelp'],
            ['draft', 'eventEditor.visibilityDraft', 'eventEditor.visibilityDraftHelp'],
            ['public_draft', 'eventEditor.visibilityPublicDraft', 'eventEditor.visibilityPublicDraftHelp']
        ].forEach(([value, labelKey, helpKey]) => {
            const choice = document.createElement('label');
            const input = document.createElement('input');
            const content = document.createElement('span');
            const title = document.createElement('strong');
            const help = document.createElement('small');

            choice.className = 'event-editor-visibility';
            input.type = 'radio';
            input.name = 'visibility';
            input.value = value;
            input.checked = value === currentVisibility;
            title.textContent = this.t(labelKey);
            help.textContent = this.t(helpKey);
            content.append(title, help);
            choice.append(input, content);
            group.append(choice);
        });
        section.append(group);
        return section;
    }

    createSection(titleKey) {
        const section = document.createElement('section');
        const title = document.createElement('h3');

        section.className = 'event-editor-section';
        title.className = 'event-editor-section-title';
        title.textContent = this.t(titleKey);
        section.append(title);
        return section;
    }

    async submit(form, submitButton, context) {
        const requiredDiscipline = form.querySelector('[data-require-one="true"]');

        if (requiredDiscipline && !requiredDiscipline.querySelector('input:checked')) {
            new Notification({
                icon: 'warning.svg',
                title: this.t('eventEditor.errorTitle'),
                content: this.t('eventEditor.disciplineRequired')
            });
            requiredDiscipline.querySelector('input')?.focus();
            return false;
        }
        if (!form.reportValidity()) return false;

        const data = new FormData(form);
        data.delete('tags');
        data.append('tags', JSON.stringify(
            [...form.querySelectorAll('input[name="tags"]:checked')].map((input) => input.value)
        ));
        if (this.event?.uid) data.set('uid', this.event.uid);

        const endpoint = this.event ? '/events/update' : '/events/insert';
        let response = null;
        const succeeded = await this.events.api.query(
            'post',
            endpoint,
            data,
            () => {
                submitButton.disabled = true;
                context.showLoading();
            },
            (result) => {
                response = result;
                context.hideLoading();
            },
            () => {},
            {
                onError: () => {
                    submitButton.disabled = false;
                    context.hideLoading();
                }
            }
        );

        if (!succeeded) return false;

        if (!this.event) {
            this.renderCreationConfirmation(context);
            await this.events.list();
            return true;
        }

        this.app.simpleModal?.close();
        new Notification({
            icon: 'check.svg',
            title: this.t('eventEditor.updateSuccessTitle'),
            content: response?.message || this.t('eventEditor.successMessage'),
            delay: 7000
        });
        await Promise.all([
            this.events.reloadActiveEventDetails(this.event.uid),
            this.events.list()
        ]);
        return true;
    }

    renderCreationConfirmation(context) {
        const box = document.createElement('div');
        const icon = document.createElement('img');
        const message = document.createElement('strong');
        const confirmation = this.t('eventEditor.createSuccessTitle');

        box.className = 'event-editor-success event-editor-confirmation-success';
        icon.className = 'event-editor-success-icon';
        icon.src = '/images/check.svg';
        icon.alt = '';
        icon.setAttribute('aria-hidden', 'true');
        message.textContent = confirmation;
        box.append(icon, message);
        context.modal.classList.add('event-editor-modal-success-state');
        context.body.replaceChildren(box);
        this.clearSuccessCloseTimer();
        this.successCloseTimer = window.setTimeout(() => {
            context.close();
        }, 1500);
    }

    clearSuccessCloseTimer() {
        if (!this.successCloseTimer) return;
        window.clearTimeout(this.successCloseTimer);
        this.successCloseTimer = null;
    }

    cleanup() {
        this.clearSuccessCloseTimer();
    }

    confirmDelete(deleteButton, context) {
        if (!this.event?.uid || !context?.body) return false;

        const editorContent = [...context.body.childNodes];
        const scroll = context.scroll;
        const previousScrollTop = scroll?.scrollTop || 0;
        const box = document.createElement('div');
        const title = document.createElement('strong');
        const message = document.createElement('p');
        const actions = document.createElement('div');
        const cancel = document.createElement('button');
        const confirm = document.createElement('button');
        const confirmIcon = document.createElement('img');
        const confirmLabel = document.createElement('span');

        box.className = 'event-editor-delete-confirmation';
        title.textContent = this.t('eventEditor.deleteConfirmTitle');
        message.textContent = this.t('eventEditor.deleteConfirmMessage');
        actions.className = 'ui-form-buttons event-editor-actions event-editor-delete-confirmation-actions';
        cancel.type = 'button';
        cancel.className = 'ui-buttons medium cancel';
        cancel.textContent = this.t('eventEditor.cancel');
        confirm.type = 'button';
        confirm.className = 'ui-buttons big delete';
        confirmIcon.src = '/images/trash-red.svg';
        confirmIcon.alt = '';
        confirmIcon.setAttribute('aria-hidden', 'true');
        confirmLabel.textContent = this.t('eventEditor.deleteConfirmAction');
        confirm.append(confirmIcon, confirmLabel);
        cancel.addEventListener('click', () => {
            context.body.replaceChildren(...editorContent);
            window.requestAnimationFrame(() => {
                if (scroll) scroll.scrollTop = previousScrollTop;
                deleteButton.focus({ preventScroll: true });
            });
        });
        confirm.addEventListener('click', () => {
            void this.delete(confirm, context);
        });
        actions.append(cancel, confirm);
        box.append(title, message, actions);
        context.body.replaceChildren(box);
        scroll?.scrollTo({ top: 0, behavior: 'smooth' });
        window.requestAnimationFrame(() => confirm.focus({ preventScroll: true }));

        return true;
    }

    async delete(deleteButton, context) {
        const uid = this.event?.uid;
        if (!uid || deleteButton.disabled) return false;

        let response = null;
        const succeeded = await this.events.api.query(
            'post',
            '/events/delete',
            { uid },
            () => {
                deleteButton.disabled = true;
                context?.showLoading();
            },
            (result) => {
                response = result;
                context?.hideLoading();
            },
            () => {},
            {
                onError: () => {
                    deleteButton.disabled = false;
                    context?.hideLoading();
                }
            }
        );

        if (!succeeded) return false;

        context?.close();
        this.events.closeEventModal();
        new Notification({
            icon: 'check.svg',
            title: this.t('eventEditor.deleteSuccessTitle'),
            content: response?.message || this.t('eventEditor.deleteSuccessMessage'),
            delay: 7000
        });
        await this.events.list();
        return true;
    }

    getCountryOptions() {
        const countries = Array.isArray(this.app.config?.countries?.list)
            ? this.app.config.countries.list
            : [];

        return [
            { value: '', label: this.t('eventEditor.countryPlaceholder'), disabled: true },
            ...countries.map((country) => ({
                value: this.normalizeCountryCode(country?.['iso-3166-1-alpha-2']),
                label: String(country?.label || '').trim()
            })).filter((country) => country.value && country.label)
        ];
    }

    normalizeCountryCode(value) {
        const code = String(value || '').trim().toUpperCase();
        return /^[A-Z]{2}$/.test(code) ? code : '';
    }

    localized(value) {
        if (!value || typeof value !== 'object') return value || '';
        return value[this.event?.source_locale] || value[this.locale] || Object.values(value)[0] || '';
    }

    t(key, parameters = {}) {
        return translate(key, this.locale, parameters);
    }
}
